import { strategyTypes } from '../../../../../constants/constants';

// --- Interfaces ---

interface RiskCategory {
    Id: string | number;
    Max: number;
    Name?: string;
    [key: string]: any;
}

interface ModelPortfolio {
    Id: string | number;
    IsActive: boolean;
    Attributes?: string; // JSON string
    RiskCategory: RiskCategory;
    [key: string]: any;
}

interface AdaptedModel extends ModelPortfolio {
    RiskScore: number;
    Type: string;
}

interface AdaptedStrategy extends RiskCategory {
    Products: (string | number)[];
    Type: string;
}

// --- Functions ---

/**
 * Parses the Attributes JSON string to extract the strategy type.
 * Defaults to STANDARD if parsing fails or key is missing.
 */
export const adaptModelType = (modelPortfolio: ModelPortfolio): string => {
    try {
        const attributes = JSON.parse(modelPortfolio?.Attributes ?? '{}');

        return attributes?.Type ?? strategyTypes.STANDARD;
    } catch (e) {
        return strategyTypes.STANDARD;
    }
};

/**
 * Filters active models and enriches them with RiskScore and Type.
 */
export const adaptModels = (models: ModelPortfolio[]): AdaptedModel[] => models.reduce((acc: AdaptedModel[], model) => {
    if (!model.IsActive) {
        return acc;
    }

    return [
        ...acc,
        {
            ...model,
            RiskScore: model.RiskCategory.Max,
            Type: adaptModelType(model),
        },
    ];
}, []);

/**
 * Groups models by their RiskCategory ID into strategies.
 */
export const adaptStrategies = (models: ModelPortfolio[]): AdaptedStrategy[] => {
    const strategies = models.reduce((acc: AdaptedStrategy[], model) => {
        if (!model.IsActive) {
            return acc;
        }

        const index = acc.findIndex(strategy => strategy.Id === model.RiskCategory.Id);

        if (index === -1) {
            return [
                ...acc,
                {
                    ...model.RiskCategory,
                    Products: [model.Id],
                    Type: adaptModelType(model),
                },
            ];
        }

        acc[index].Products.push(model.Id);

        return acc;
    }, []);

    // Sort by Risk Max ascending
    return strategies.sort((a, b) => a.Max - b.Max);
};
